/**
 * Stripe integration module for Ingrediable.
 *
 * This provides the client-side Stripe checkout flow.
 * To enable, set VITE_STRIPE_PUBLISHABLE_KEY in your .env and deploy
 * the create-checkout-session edge function.
 *
 * Usage:
 *   import { redirectToCheckout, PLANS } from "./lib/stripe";
 *   await redirectToCheckout("monthly", userId);
 */

export const PLANS = {
  free: {
    name: "Free",
    price: 0,
    interval: null,
    features: [
      "Up to 50 inventory items",
      "Barcode scanning",
      "Basic shopping lists",
      "1 household member",
    ],
    limits: {
      maxItems: 50,
      maxHouseholdMembers: 1,
      receiptScanning: false,
      wasteAnalytics: false,
      circles: false,
      mealPlanner: false,
    },
  },
  monthly: {
    name: "Pro",
    price: 7.99,
    interval: "month" as const,
    priceId: (import.meta as any).env?.VITE_STRIPE_PRICE_MONTHLY || "",
    features: [
      "Whole household included (up to 6 people)",
      "Unlimited inventory items",
      "AI receipt scanning",
      "Meal planner & recipes",
      "Advanced waste analytics",
      "Circles (community sharing)",
      "Priority support",
    ],
    limits: {
      maxItems: Infinity,
      maxHouseholdMembers: 6,
      receiptScanning: true,
      wasteAnalytics: true,
      circles: true,
      mealPlanner: true,
    },
  },
  yearly: {
    name: "Pro (Annual)",
    price: 59.99,
    interval: "year" as const,
    priceId: (import.meta as any).env?.VITE_STRIPE_PRICE_YEARLY || "",
    features: [
      "Everything in Pro",
      "Save $35.89/year (37% off)",
    ],
    limits: {
      maxItems: Infinity,
      maxHouseholdMembers: 6,
      receiptScanning: true,
      wasteAnalytics: true,
      circles: true,
      mealPlanner: true,
    },
  },
  business: {
    name: "Business",
    price: 29.99,
    interval: "month" as const,
    features: [
      "Everything in Pro",
      "Unlimited household members",
      "CSV/PDF export for compliance",
      "Multi-location inventory",
      "Dedicated support",
      "API access (coming soon)",
    ],
    limits: {
      maxItems: Infinity,
      maxHouseholdMembers: Infinity,
      receiptScanning: true,
      wasteAnalytics: true,
      circles: true,
      mealPlanner: true,
    },
  },
};

export type PlanKey = keyof typeof PLANS;

/**
 * Check if a feature is available for the user's current plan.
 */
export function canAccess(userPlan: PlanKey, feature: keyof typeof PLANS.free.limits): boolean {
  return !!PLANS[userPlan]?.limits?.[feature];
}

/**
 * Check if user has hit the item limit for their plan.
 */
export function isAtItemLimit(userPlan: PlanKey, currentItemCount: number): boolean {
  const limit = PLANS[userPlan]?.limits?.maxItems ?? 50;
  return currentItemCount >= limit;
}

/**
 * Redirect to Stripe Checkout.
 * Requires a Supabase Edge Function `create-checkout-session` to be deployed.
 */
export async function redirectToCheckout(plan: "monthly" | "yearly", userId: string): Promise<void> {
  const key = (import.meta as any).env?.VITE_STRIPE_PUBLISHABLE_KEY;
  if (!key) {
    console.error("[Stripe] VITE_STRIPE_PUBLISHABLE_KEY not set.");
    throw new Error("Payments are not configured yet. Please contact support.");
  }

  const priceId = PLANS[plan].priceId;
  if (!priceId) {
    throw new Error("Price ID not configured for this plan.");
  }

  // Call Supabase Edge Function to create a checkout session
  const { supabase } = await import("./supabase-client").catch(() => ({ supabase: null }));
  if (!supabase) {
    throw new Error("Not connected to server.");
  }

  // Always get a fresh token by calling the auth REST API directly.
  // This bypasses the SDK's internal async lock entirely, so there's no risk
  // of deadlocking with the autoRefreshToken background process.
  // It also handles the case where the JWT signing key was rotated, which
  // would make a locally non-expired token invalid on the server.
  const { data: sessionData } = await (supabase as any).auth.getSession();
  const refreshToken: string | undefined = sessionData?.session?.refresh_token;
  const supabaseUrl: string = (supabase as any).supabaseUrl ?? (import.meta as any).env?.VITE_SUPABASE_URL ?? "";
  const supabaseKey: string = (supabase as any).supabaseKey ?? (import.meta as any).env?.VITE_SUPABASE_ANON_KEY ?? "";

  let accessToken: string | undefined;

  if (refreshToken && supabaseUrl && supabaseKey) {
    try {
      console.log("[Stripe] refreshing token via REST API...");
      const refreshResult = await Promise.race([
        fetch(`${supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
          method: "POST",
          headers: { "Content-Type": "application/json", apikey: supabaseKey },
          body: JSON.stringify({ refresh_token: refreshToken }),
        }).then((r) => r.json()),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000)),
      ]);
      if (refreshResult?.access_token) {
        accessToken = refreshResult.access_token;
        console.log("[Stripe] token refreshed successfully");
      } else {
        console.warn("[Stripe] refresh response missing access_token:", refreshResult);
      }
    } catch (e) {
      console.warn("[Stripe] REST token refresh failed:", e);
    }
  }

  // Fall back to the cached token if refresh failed
  if (!accessToken) {
    accessToken = sessionData?.session?.access_token;
  }

  if (!accessToken) {
    throw new Error("Your session has expired. Please refresh the page and sign in again.");
  }

  // The Supabase edge function relay verifies JWTs using the project's HS256 secret.
  // User sessions are now signed with ES256, which the relay cannot verify → 401.
  // The anon key is an HS256 JWT the relay accepts. The function gets userId from the
  // request body, so no auth context is lost.
  const { data, error } = await (supabase as any).functions.invoke("create-checkout-session", {
    body: { priceId, userId, plan },
    headers: { Authorization: `Bearer ${supabaseKey}` },
  });

  if (error || !data?.url) {
    // Log the full error so the real cause is always visible in devtools
    console.error("[Stripe] checkout error:", error, "context status:", error?.context?.status, error?.context?.statusText);

    let detail = error?.message || "Failed to create checkout session.";
    try {
      // FunctionsHttpError.context is the raw Response — read the actual body.
      // Race against 5s; if the stream hangs we at least unblock quickly.
      const context = error?.context;
      if (context && typeof context.text === "function") {
        const text = await Promise.race([
          context.text() as Promise<string>,
          new Promise<string>((_, reject) => setTimeout(() => reject(new Error("timeout")), 5000)),
        ]);
        console.error("[Stripe] response body:", text);
        const parsed = JSON.parse(text);
        if (parsed?.error) detail = parsed.error;
        else if (parsed?.message) detail = parsed.message;
      }
    } catch (readErr) {
      console.error("[Stripe] could not read response body:", readErr);
    }
    throw new Error(detail);
  }

  window.location.href = data.url;
}

/**
 * Redirect to Stripe Customer Portal for subscription management.
 */
export async function redirectToCustomerPortal(userId: string): Promise<void> {
  const { supabase } = await import("./supabase-client").catch(() => ({ supabase: null }));
  if (!supabase) {
    throw new Error("Not connected to server.");
  }

  const supabaseKey: string = (supabase as any).supabaseKey ?? (import.meta as any).env?.VITE_SUPABASE_ANON_KEY ?? "";

  const { data, error } = await (supabase as any).functions.invoke("create-portal-session", {
    body: { userId },
    headers: { Authorization: `Bearer ${supabaseKey}` },
  });

  if (error || !data?.url) {
    throw new Error(error?.message || "Failed to open billing portal.");
  }

  window.location.href = data.url;
}
